/**
 * ImageGridBlock: `image-grid` content block.
 *
 * data: { images: [{ url, alt, caption? }] }
 *
 * A responsive grid of authored images. Degrades gracefully: a missing /
 * non-array / empty `images` renders nothing, and individual entries without
 * a `url` (or with a non-allowlisted url) are skipped so one bad entry does
 * not break the grid.
 *
 * Round-3 review finding 2: per-item URL allowlist mirrors HeroBlock /
 * CtaButtonBlock / ImageBlock so the four author-content blocks with URLs
 * defend themselves uniformly. The server's validateBlockUrls is the real
 * security boundary; the per-item filter is defense-in-depth for any code
 * path that bypasses it (raw migration, admin tool, legacy row).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Figure } from 'react-bootstrap';

import { isAllowedImageUrl } from '../utils/urlValidator';

export default function ImageGridBlock({ data }) {
    const { images } = data || {};

    if (!Array.isArray(images)) {
        return null;
    }

    // Skip half-authored entries (no url) AND non-allowlisted urls rather
    // than crash or render a tainted src.
    const validImages = images.filter(
        (img) => img && img.url && isAllowedImageUrl(img.url)
    );
    if (validImages.length === 0) {
        return null;
    }

    return (
        <Row className="g-3">
            {validImages.map((img, index) => (
                // url+index is a stable key for this render-only,
                // non-reorderable authored list.
                <Col key={`${img.url}-${index}`} xs={12} sm={6} md={4}>
                    <Figure className="d-block w-100 mb-0">
                        <Figure.Image
                            src={img.url}
                            alt={img.alt || ''}
                            className="content-image-grid__img rounded"
                        />
                        {img.caption && (
                            <Figure.Caption className="text-muted mt-1 small">
                                {img.caption}
                            </Figure.Caption>
                        )}
                    </Figure>
                </Col>
            ))}
        </Row>
    );
}

ImageGridBlock.propTypes = {
    data: PropTypes.shape({
        images: PropTypes.arrayOf(
            PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
                caption: PropTypes.string,
            })
        ),
    }),
};

ImageGridBlock.defaultProps = {
    data: {},
};
